import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import en from '../locales/en';
import ar from '../locales/ar';
import ur from '../locales/ur';
import fa from '../locales/fa';

const STORAGE_KEY = 'unit_c_locale';
const DEFAULT_LOCALE = 'en';
const RTL_LOCALES = new Set(['ar', 'ur', 'fa', 'he']);

const dictionaries = {
  en,
  ar,
  ur,
  fa
};

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;

  return path.split('.').reduce((acc, segment) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, segment)) {
      return acc[segment];
    }

    return undefined;
  }, obj);
};

const flattenStringValues = (obj, prefix = '', acc = {}) => {
  if (!obj || typeof obj !== 'object') return acc;

  Object.entries(obj).forEach(([key, value]) => {
    const nextPath = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      acc[nextPath] = value;
      return;
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenStringValues(value, nextPath, acc);
    }
  });

  return acc;
};

const interpolate = (template, params = {}) => {
  if (typeof template !== 'string') return template;

  return template.replace(/\{(\w+)\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      return String(params[key]);
    }

    return match;
  });
};

let globalTranslator = (key, _params = {}, fallback = '') => fallback || key;

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  direction: 'ltr',
  setLocale: () => { },
  t: (key, _params = {}, fallback = '') => fallback || key,
  translateLiteral: (value) => value
});

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved || DEFAULT_LOCALE;
  });

  const direction = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
  const dictionary = dictionaries[locale] || dictionaries[DEFAULT_LOCALE];
  const literalTranslationMaps = useMemo(() => {
    const fallbackFlat = flattenStringValues(dictionaries[DEFAULT_LOCALE]);
    const localeFlat = flattenStringValues(dictionary);
    const exactMap = new Map();
    const caseInsensitiveMap = new Map();

    Object.keys(fallbackFlat).forEach((path) => {
      const english = fallbackFlat[path];
      const localized = localeFlat[path];

      if (typeof english !== 'string' || typeof localized !== 'string') return;

      const source = english.trim();
      const target = localized.trim();

      if (!source) return;

      exactMap.set(source, target);
      caseInsensitiveMap.set(source.toLowerCase(), target);
    });

    return { exactMap, caseInsensitiveMap };
  }, [dictionary]);

  const t = useCallback(
    (key, params = {}, fallback = '') => {
      const localizedValue = getNestedValue(dictionary, key);
      const fallbackValue = getNestedValue(dictionaries[DEFAULT_LOCALE], key);
      const resolved = localizedValue ?? fallbackValue ?? fallback ?? key;

      if (typeof resolved === 'string') {
        return interpolate(resolved, params);
      }

      return fallback || key;
    },
    [dictionary]
  );

  const translateLiteral = useCallback(
    (value) => {
      if (typeof value !== 'string') return value;

      const leadingSpaces = value.match(/^\s*/)?.[0] || '';
      const trailingSpaces = value.match(/\s*$/)?.[0] || '';
      const trimmed = value.trim();

      if (!trimmed) return value;

      const exactMatch = literalTranslationMaps.exactMap.get(trimmed);
      const ciMatch = literalTranslationMaps.caseInsensitiveMap.get(trimmed.toLowerCase());
      const translated = exactMatch || ciMatch;

      if (!translated) return value;

      return `${leadingSpaces}${translated}${trailingSpaces}`;
    },
    [literalTranslationMaps]
  );

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    document.body.setAttribute('dir', direction);
    localStorage.setItem(STORAGE_KEY, locale);
  }, [locale, direction]);

  // Dynamically inject RTL locale fonts to avoid loading them for all users
  useEffect(() => {
    const FONT_LINK_ID = 'unit-c-rtl-font';
    const existing = document.getElementById(FONT_LINK_ID);
    if (existing) existing.remove();

    const fontUrls = {
      ar: 'https://fonts.googleapis.com/css2?family=Tajawal:wght@300;400;500;700&display=swap',
      ur: 'https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu:wght@400;700&display=swap',
      fa: 'https://fonts.googleapis.com/css2?family=Vazirmatn:wght@100;200;300;400;500;600;700;800;900&display=swap'
    };

    if (fontUrls[locale]) {
      const link = document.createElement('link');
      link.id = FONT_LINK_ID;
      link.rel = 'stylesheet';
      link.href = fontUrls[locale];
      document.head.appendChild(link);
    }
  }, [locale]);

  useEffect(() => {
    globalTranslator = t;

    return () => {
      globalTranslator = (key, _params = {}, fallback = '') => fallback || key;
    };
  }, [t]);

  const setLocale = useCallback((newLocale) => {
    setLocaleState(newLocale || DEFAULT_LOCALE);
  }, []);

  const value = useMemo(
    () => ({
      locale,
      direction,
      setLocale,
      t,
      translateLiteral
    }),
    [locale, direction, setLocale, t, translateLiteral]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);

export const translate = (key, params = {}, fallback = '') => {
  return globalTranslator(key, params, fallback);
};

export const isRtlLocale = (localeCode) => RTL_LOCALES.has(localeCode);
